import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Zap, Target, Award, Clock, DollarSign, Users, AlertTriangle, XCircle, UserX, Calendar, Lightbulb } from 'lucide-react';

interface Insight {
  type: 'success' | 'warning' | 'info' | 'danger';
  icon: React.ReactNode;
  title: string;
  description: string;
  value?: string;
  trend?: 'up' | 'down';
  trendValue?: string;
}

interface SmartInsightsProps {
  insights: Insight[];
}

export function SmartInsights({ insights }: SmartInsightsProps) {
  const getColorClasses = (type: string) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: 'text-green-600',
          text: 'text-green-900',
          badge: 'bg-green-100 text-green-700'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          icon: 'text-amber-600',
          text: 'text-amber-900',
          badge: 'bg-amber-100 text-amber-700'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'text-blue-600',
          text: 'text-blue-900',
          badge: 'bg-blue-100 text-blue-700'
        };
      case 'danger':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-600',
          text: 'text-red-900',
          badge: 'bg-red-100 text-red-700'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          icon: 'text-gray-600',
          text: 'text-gray-900',
          badge: 'bg-gray-100 text-gray-700'
        };
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm"
    >
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="p-2 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl">
          <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">תובנות חכמות</h3>
      </div>

      <div className="space-y-3">
        {insights.map((insight, index) => {
          const colors = getColorClasses(insight.type);
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className={`p-4 rounded-xl border ${colors.bg} ${colors.border} hover:shadow-md transition-all duration-300`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg bg-white ${colors.icon}`}>
                  {insight.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-semibold ${colors.text}`}>{insight.title}</h4>
                    {insight.value && (
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}>
                        {insight.value}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{insight.description}</p>
                  {insight.trend && insight.trendValue && (
                    <div className="flex items-center gap-2 mt-2">
                      {insight.trend === 'up' ? (
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-600" />
                      )}
                      <span className={`text-sm font-semibold ${insight.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {insight.trendValue}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

export function generateInsights(statsData: any, previousData?: any): Insight[] {
  const insights: Insight[] = [];

  if (!statsData) return insights;

  const completionRate = statsData.monthly.total > 0
    ? Math.round((statsData.monthly.completed / statsData.monthly.total) * 100)
    : 0;

  if (statsData.monthly.total > 0) {
    if (completionRate >= 90) {
      insights.push({
        type: 'success',
        icon: <Award className="w-5 h-5" />,
        title: 'שיעור השלמה מצוין!',
        description: `${completionRate}% מהתורים החודש הושלמו בהצלחה. כל הכבוד!`,
        value: `${completionRate}%`,
      });
    } else if (completionRate < 70) {
      insights.push({
        type: 'warning',
        icon: <Target className="w-5 h-5" />,
        title: 'שיעור השלמה נמוך',
        description: `${completionRate}% מהתורים הושלמו. כדאי לשלוח תזכורות ללקוחות ולבדוק מדוע תורים מבוטלים.`,
        value: `${completionRate}%`,
      });
    } else {
      insights.push({
        type: 'info',
        icon: <Target className="w-5 h-5" />,
        title: 'שיעור השלמה סולידי',
        description: `${completionRate}% מהתורים הושלמו החודש - ביצועים טובים!`,
        value: `${completionRate}%`,
      });
    }
  }

  if (previousData && previousData.monthly.revenue > 0) {
    const revenueChange = ((statsData.monthly.revenue - previousData.monthly.revenue) / previousData.monthly.revenue) * 100;
    if (Math.abs(revenueChange) > 10) {
      insights.push({
        type: revenueChange > 0 ? 'success' : 'warning',
        icon: <DollarSign className="w-5 h-5" />,
        title: revenueChange > 0 ? 'עלייה משמעותית בהכנסות!' : 'ירידה בהכנסות',
        description: `ההכנסות ${revenueChange > 0 ? 'עלו' : 'ירדו'} ב-${Math.abs(Math.round(revenueChange))}% לעומת החודש הקודם${revenueChange < 0 ? '. כדאי לבדוק מה השתנה' : '!'}`,
        value: `${new Intl.NumberFormat('he-IL').format(statsData.monthly.revenue)}₪`,
        trend: revenueChange > 0 ? 'up' : 'down',
        trendValue: `${Math.abs(Math.round(revenueChange))}%`,
      });
    }
  }

  const avgRevenue = statsData.monthly.completed > 0
    ? Math.round(statsData.monthly.revenue / statsData.monthly.completed)
    : 0;

  if (avgRevenue > 0 && insights.length < 3) {
    if (avgRevenue >= 200) {
      insights.push({
        type: 'success',
        icon: <TrendingUp className="w-5 h-5" />,
        title: 'הכנסה ממוצעת מעולה!',
        description: `${avgRevenue}₪ לתור בממוצע - זה מצוין! כדאי להתמקד בשירותים רווחיים אלה.`,
        value: `${avgRevenue}₪`,
      });
    } else if (avgRevenue < 100 && statsData.monthly.completed > 5) {
      insights.push({
        type: 'info',
        icon: <TrendingUp className="w-5 h-5" />,
        title: 'הכנסה ממוצעת נמוכה',
        description: `${avgRevenue}₪ לתור בממוצע. שקול להעלות מחירים או להציע שירותים נוספים.`,
        value: `${avgRevenue}₪`,
      });
    }
  }

  if (previousData && insights.length < 3) {
    const weeklyGrowth = previousData.weekly.revenue > 0
      ? ((statsData.weekly.revenue - previousData.weekly.revenue) / previousData.weekly.revenue) * 100
      : 0;

    if (Math.abs(weeklyGrowth) > 15) {
      insights.push({
        type: weeklyGrowth > 0 ? 'success' : 'warning',
        icon: <Clock className="w-5 h-5" />,
        title: weeklyGrowth > 0 ? 'שבוע מוצלח!' : 'שבוע מאתגר',
        description: `${statsData.weekly.completed} תורים הושלמו השבוע, ${Math.abs(Math.round(weeklyGrowth))}% ${weeklyGrowth > 0 ? 'יותר' : 'פחות'} מהשבוע הקודם.`,
        trend: weeklyGrowth > 0 ? 'up' : 'down',
        trendValue: `${Math.abs(Math.round(weeklyGrowth))}%`,
      });
    }
  }

  const cancellationRate = statsData.monthly.total > 0
    ? Math.round((statsData.monthly.cancelled / statsData.monthly.total) * 100)
    : 0;

  if (cancellationRate > 15 && insights.length < 5) {
    insights.push({
      type: 'danger',
      icon: <XCircle className="w-5 h-5" />,
      title: 'שיעור ביטולים גבוה!',
      description: `${cancellationRate}% מהתורים בוטלו החודש (${statsData.monthly.cancelled} תורים). כדאי לשלוח תזכורות יום לפני התור ולבדוק אם יש בעיה חוזרת.`,
      value: `${cancellationRate}%`,
    });
  } else if (cancellationRate > 10 && cancellationRate <= 15 && insights.length < 5) {
    insights.push({
      type: 'warning',
      icon: <XCircle className="w-5 h-5" />,
      title: 'ביטולים מעל הממוצע',
      description: `${cancellationRate}% מהתורים בוטלו החודש. שקול להוסיף תזכורות אוטומטיות או מדיניות ביטול.`,
      value: `${cancellationRate}%`,
    });
  }

  const noShowRate = statsData.monthly.total > 0
    ? Math.round((statsData.monthly.noShow / statsData.monthly.total) * 100)
    : 0;

  if (noShowRate > 10 && insights.length < 5) {
    insights.push({
      type: 'danger',
      icon: <UserX className="w-5 h-5" />,
      title: 'אזהרה: לקוחות לא מגיעים!',
      description: `${noShowRate}% מהלקוחות לא הגיעו לתור (${statsData.monthly.noShow} תורים). מומלץ מאוד לדרוש פיקדון או אישור יום לפני.`,
      value: `${statsData.monthly.noShow} תורים`,
    });
  } else if (noShowRate > 5 && noShowRate <= 10 && insights.length < 5) {
    insights.push({
      type: 'warning',
      icon: <UserX className="w-5 h-5" />,
      title: 'לקוחות שלא הגיעו',
      description: `${noShowRate}% מהלקוחות לא הגיעו לתור החודש. שקול להוסיף תזכורות SMS ביום התור.`,
      value: `${statsData.monthly.noShow} תורים`,
    });
  }

  if (statsData.monthly.completed > 0 && insights.length < 5) {
    const lostRevenue = (statsData.monthly.cancelled + statsData.monthly.noShow) * avgRevenue;
    if (lostRevenue > 500) {
      insights.push({
        type: 'warning',
        icon: <AlertTriangle className="w-5 h-5" />,
        title: 'הכנסה שאבדה',
        description: `בגלל ביטולים ואי-הגעות, איבדת כ-${Math.round(lostRevenue).toLocaleString('he-IL')}₪ החודש. שיפור בניהול התורים יכול להגדיל משמעותית את ההכנסות.`,
        value: `₪${Math.round(lostRevenue).toLocaleString('he-IL')}`,
      });
    }
  }

  if (statsData.daily.total > 0 && statsData.daily.completed === statsData.daily.total && insights.length < 5) {
    insights.push({
      type: 'success',
      icon: <Award className="w-5 h-5" />,
      title: 'יום מושלם!',
      description: `כל ${statsData.daily.total} התורים היום הושלמו בהצלחה. אין ביטולים ואין אי-הגעות - המשך כך!`,
      value: '100%',
    });
  }

  if (previousData && previousData.monthly.completed > 0 && insights.length < 5) {
    const appointmentGrowth = ((statsData.monthly.completed - previousData.monthly.completed) / previousData.monthly.completed) * 100;
    if (appointmentGrowth > 20) {
      insights.push({
        type: 'success',
        icon: <Calendar className="w-5 h-5" />,
        title: 'צמיחה מרשימה!',
        description: `${Math.round(appointmentGrowth)}% יותר תורים הושלמו החודש לעומת החודש הקודם. העסק שלך צומח!`,
        value: `+${Math.round(appointmentGrowth)}%`,
        trend: 'up',
        trendValue: `${statsData.monthly.completed} תורים`,
      });
    } else if (appointmentGrowth < -20) {
      insights.push({
        type: 'warning',
        icon: <TrendingDown className="w-5 h-5" />,
        title: 'ירידה במספר התורים',
        description: `${Math.abs(Math.round(appointmentGrowth))}% פחות תורים הושלמו החודש. כדאי לבדוק אם יש בעיה או לשקול קמפיין שיווקי.`,
        value: `-${Math.abs(Math.round(appointmentGrowth))}%`,
        trend: 'down',
        trendValue: `${statsData.monthly.completed} תורים`,
      });
    }
  }

  if (statsData.monthly.totalDuration && statsData.monthly.completed > 0 && insights.length < 5) {
    const avgDuration = Math.round(statsData.monthly.totalDuration / statsData.monthly.completed);
    if (avgDuration < 30 && statsData.monthly.completed > 10) {
      insights.push({
        type: 'info',
        icon: <Clock className="w-5 h-5" />,
        title: 'תורים קצרים',
        description: `התורים שלך נמשכים ${avgDuration} דקות בממוצע. אם אתה מספיק מהיר, אפשר להוסיף עוד תורים ביום!`,
        value: `${avgDuration} דק'`,
      });
    } else if (avgDuration > 90 && statsData.monthly.completed > 10) {
      insights.push({
        type: 'info',
        icon: <Clock className="w-5 h-5" />,
        title: 'תורים ארוכים',
        description: `התורים שלך נמשכים ${avgDuration} דקות בממוצע. שקול לחלק לשירותים קצרים יותר או להעלות מחירים.`,
        value: `${avgDuration} דק'`,
      });
    }
  }

  if (statsData.weekly.completed > 0 && statsData.weekly.total > 0 && insights.length < 5) {
    const weeklyCompletion = Math.round((statsData.weekly.completed / statsData.weekly.total) * 100);
    if (weeklyCompletion < completionRate - 10) {
      insights.push({
        type: 'warning',
        icon: <AlertTriangle className="w-5 h-5" />,
        title: 'שבוע מאתגר',
        description: `רק ${weeklyCompletion}% מהתורים השבוע הושלמו, נמוך מהממוצע שלך. האם יש משהו שהשתנה?`,
        value: `${weeklyCompletion}%`,
      });
    } else if (weeklyCompletion > completionRate + 10) {
      insights.push({
        type: 'success',
        icon: <Award className="w-5 h-5" />,
        title: 'שבוע מעולה!',
        description: `${weeklyCompletion}% מהתורים השבוע הושלמו - גבוה מהממוצע שלך. המשך כך!`,
        value: `${weeklyCompletion}%`,
      });
    }
  }

  if (statsData.monthly.total < 10 && insights.length === 0) {
    insights.push({
      type: 'info',
      icon: <Lightbulb className="w-5 h-5" />,
      title: 'התחלה חדשה',
      description: `רק ${statsData.monthly.total} תורים החודש. המערכת תציג תובנות מפורטות יותר ככל שיהיו יותר נתונים.`,
    });
  }

  return insights.slice(0, 5);
}
